/* Move complete native Shopify sections, retaining their editor ownership. */
(() => {
  if (customElements.get('terror-sidebar-layout')) return;
  const SECTION = '.gt-sidebar-section';
  const MODULE = '[data-gt-sidebar-module]';
  const FRAME_CLASSES = ['gt-boxed-module', 'gt-header-module', 'gt-category-module', 'gt-joined-first', 'gt-joined-last', 'gt-before-header'];

  class TerrorSidebarLayout extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      if (document.readyState === 'loading' && !window.terrorLayoutParsed) {
        document.addEventListener('terror:layout-ready', () => this.start(), {once: true, signal});
      } else this.start();
    }

    start() {
      if (!this.isConnected || this.started) return;
      this.aside = this.querySelector('.gt-sidebar');
      this.main = this.querySelector('#MainContent');
      this.column = this.querySelector('.gt-main-column');
      if (!this.aside || !this.main || !this.column) return;
      this.started = true;
      this.entries = new Map();
      this.zones = new Map();
      this.mobileLogos = new Map();
      this.retired = new WeakSet();
      this.selected = null;
      this.media = matchMedia(`(min-width: ${Number(this.dataset.breakpoint) || 1200}px)`);
      const signal = this.abort.signal;
      this.media.addEventListener('change', () => this.place(), {signal});
      this.addEventListener('click', e => {
        if (this.dataset.editor === 'true' && e.target.closest?.(`${SECTION} a`)) e.preventDefault();
      }, {capture: true, signal});
      ['load', 'unload', 'reorder'].forEach(type => {
        document.addEventListener(`shopify:section:${type}`, e => this.onSectionEvent(type, e), {signal});
      });
      document.addEventListener('shopify:section:select', e => this.selectSection(e), {signal});
      document.addEventListener('shopify:block:select', e => this.selectSection(e), {signal});
      document.addEventListener('shopify:section:deselect', e => {
        if (e.target.closest?.(SECTION)?.id === this.selected) {
          this.selected = null;
          this.place();
        }
      }, {signal});
      this.place();
    }

    onSectionEvent(type, event) {
      const node = event.target.closest?.(SECTION);
      if (node && this.contains(node)) {
        const entry = this.entries.get(node.id);
        if (type === 'unload' && entry?.node === node) {
          // Put a mobile section back at its native group position before Shopify
          // removes/replaces it. Never keep a live block in a detached template.
          this.returnHome(entry);
          this.retired.add(node);
        } else if (type === 'reorder' && entry && node.parentNode === this.aside) {
          this.aside.insertBefore(entry.home, node);
        }
      }
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        if (this.isConnected) this.place();
      }, 0);
    }

    scan() {
      const candidates = [...this.querySelectorAll(SECTION)].filter(node => node.querySelector(MODULE) && !this.retired.has(node));
      const live = new Map(candidates.map(node => [node.id, node]));
      for (const [id, entry] of this.entries) {
        if (live.get(id) === entry.node) {
          entry.module = entry.node.querySelector(MODULE);
          continue;
        }
        const replacement = live.get(id);
        if (replacement) {
          entry.node = replacement;
          entry.module = replacement.querySelector(MODULE);
        } else {
          entry.home.remove();
          this.entries.delete(id);
          if (this.selected === id) this.selected = null;
        }
      }
      for (const node of candidates) {
        if (this.entries.has(node.id)) continue;
        const home = document.createComment(` sidebar section: ${node.id} `);
        if (node.parentNode === this.aside) node.before(home);
        else this.aside.append(home);
        this.entries.set(node.id, {node, module: node.querySelector(MODULE), home});
      }
      const homes = new Map([...this.entries.values()].map(entry => [entry.home, entry]));
      return [...this.aside.childNodes].map(node => homes.get(node)).filter(Boolean);
    }

    returnHome(entry) {
      if (entry.home.parentNode && entry.home.nextSibling !== entry.node) entry.home.after(entry.node);
      entry.node.removeAttribute('data-gt-mobile-section');
    }

    zone(position) {
      if (!['before_header', 'after_header', 'after_carousels'].includes(position)) position = 'after_carousels';
      if (!this.zones.has(position)) {
        const zone = document.createElement('div');
        zone.className = 'gt-mobile-widgets';
        zone.dataset.widgetZone = position;
        zone.style.setProperty('--gt-mobile-gap', `${Number(this.dataset.mobileGap) || 0}px`);
        zone.style.setProperty('--gt-mobile-padding', `${Number(this.dataset.mobilePadding) || 0}px`);
        this.zones.set(position, zone);
      }
      return this.zones.get(position);
    }

    placeZones() {
      const header = this.main.querySelector('terror-video-header')?.closest('.shopify-section');
      const lastCarousel = [...this.main.querySelectorAll('terror-video-carousel')].at(-1)?.closest('.shopify-section');
      for (const [position, zone] of this.zones) {
        if (!zone.childElementCount) { zone.remove(); continue; }
        if (position === 'before_header') {
          const anchor = header?.parentNode === this.main ? header : this.main.firstElementChild;
          if (anchor !== zone && zone.nextElementSibling !== anchor) this.main.insertBefore(zone, anchor);
        } else {
          const anchor = position === 'after_header' ? header : lastCarousel;
          if (anchor?.parentNode === this.main) {
            if (anchor.nextElementSibling !== zone) anchor.after(zone);
          } else if (zone !== this.main.lastElementChild) this.main.append(zone);
        }
      }
    }

    restoreLogo(link, record) {
      link.replaceChildren(record.original);
      link.removeAttribute('data-gt-logo-swapped');
      link.removeAttribute('data-gt-logo-border-hidden');
      this.mobileLogos.delete(link);
    }

    placeMobileLogo(brand, enabled) {
      const template = enabled ? brand?.querySelector('template[data-mobile-logo]') : null;
      for(const link of this.column.querySelectorAll('[data-gt-header-logo]'))if(link.gtEarlyLogo){this.mobileLogos.set(link,link.gtEarlyLogo);delete link.gtEarlyLogo;}
      for (const [link, record] of this.mobileLogos) {
        if (!link.isConnected || record.template !== template) this.restoreLogo(link, record);
      }
      if (!template?.content.firstElementChild) return;
      for (const link of this.column.querySelectorAll('[data-gt-header-logo]')) {
        if (!this.mobileLogos.has(link)) {
          const original = document.createDocumentFragment();
          while (link.firstChild) original.append(link.firstChild);
          this.mobileLogos.set(link, {original, template});
          link.append(template.content.firstElementChild.cloneNode(true));
          link.setAttribute('data-gt-logo-swapped', '');
        }
        link.dataset.gtLogoBorderHidden = String(brand.dataset.mobileLogoBorder === 'false');
      }
    }

    frameModules(active) {
      const sections = [...this.column.querySelectorAll('#MainContent > .shopify-section, .shopify-section-group-footer-group')];
      for (const section of sections) {
        section.classList.remove(...FRAME_CLASSES);
        if (!active || section.matches(SECTION) || section.querySelector('terror-video-carousel')) continue;
        if (section.querySelector('terror-video-header')) section.classList.add('gt-header-module');
        else if (section.querySelector('.featured-navigation')) section.classList.add('gt-category-module');
        else if ([...section.children].some(el => !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(el.tagName))) section.classList.add('gt-boxed-module');
      }
      if (active) {
        let previous;
        for (const section of sections) {
          if (section.classList.contains('gt-header-module')) previous?.classList.add('gt-before-header');
          if (section.matches('.gt-boxed-module,.gt-header-module,.gt-category-module') || section.querySelector('terror-video-carousel')) previous = section;
        }
      }
      if (active && this.dataset.joinBars === 'true') {
        for (const section of sections) {
          const next = section.nextElementSibling;
          if (section.querySelector('[data-gt-banner]') && next?.querySelector('[data-gt-banner]')) {
            section.classList.add('gt-joined-first');
            next.classList.add('gt-joined-last');
          }
        }
      }
    }

    place() {
      if (!this.started) return;
      const entries = this.scan();
      const active = this.dataset.active === 'true';
      const desktop = this.media.matches;
      const brand = entries.find(entry => entry.module.dataset.kind === 'brand')?.module;
      this.frameModules(active);
      this.placeMobileLogo(brand, !desktop || !active);
      if (!desktop) brand?.querySelectorAll('[x-ref="searchComponent"] button[type="button"]').forEach(button => {
        // Close an open desktop search before returning to the original mobile header.
        if (button.closest('[x-ref="searchComponent"]').style.display !== 'none') button.click();
      });
      this.column.classList.toggle('gt-header-in-sidebar', active && desktop && brand?.dataset.moveHeader === 'true' && !!brand.querySelector('.gt-sidebar-navigation'));
      const mobile = new Map();
      for (const entry of entries) {
        const {node, module} = entry;
        const previewSelected = this.dataset.editor === 'true' && this.selected === node.id && module.dataset.kind !== 'brand';
        const visible = active && (desktop || this.dataset.mobileWidgets === 'true') && (module.dataset[desktop ? 'desktop' : 'mobile'] === 'true' || previewSelected);
        node.hidden = !visible;
        if (!visible) module.querySelectorAll('video, audio').forEach(media => media.pause());
        if (visible && !desktop) {
          const position=module.dataset.kind==='chat'?(entries.find(e=>e.module.dataset.kind==='deck')?.module.dataset.position||'after_carousels'):module.dataset.position;
          const zone = this.zone(position);
          if (!mobile.has(zone)) mobile.set(zone, []);
          mobile.get(zone).push(node);
          node.dataset.gtMobileSection = 'true';
        } else this.returnHome(entry);
      }
      for (const [zone, nodes] of mobile) {
        const chats=nodes.filter(n=>n.querySelector('[data-kind=chat]'));for(const chat of chats){nodes.splice(nodes.indexOf(chat),1);const deck=nodes.findLastIndex(n=>n.querySelector('[data-kind=deck]'));nodes.splice(deck<0?nodes.length:deck+1,0,chat);}
        nodes.forEach((node, index) => {
          const current = zone.children[index];
          if (current !== node) zone.insertBefore(node, current || null);
        });
      }
      this.placeZones();
      window.terrorSurfaces?.term?.updateModal();
      window.terrorSurfaces?.fitSidebar();
      window.terrorSurfaces?.layout();
    }

    selectSection(event) {
      const node = event.target.closest?.(SECTION);
      if (!node || !this.contains(node)) return;
      this.selected = node.id;
      this.place();
      node.scrollIntoView?.({block: 'nearest', inline: 'nearest', behavior: 'auto'});
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.refreshTimer);
      this.entries?.forEach(entry => { this.returnHome(entry); entry.node.hidden = false; entry.home.remove(); });
      this.zones?.forEach(zone => zone.remove());
      this.mobileLogos?.forEach((record, link) => this.restoreLogo(link, record));
      this.column?.classList.remove('gt-header-in-sidebar');
      this.column?.querySelectorAll('.gt-boxed-module, .gt-header-module, .gt-category-module').forEach(node => node.classList.remove(...FRAME_CLASSES));
      this.abort = null;
      this.started = false;
    }
  }
  customElements.define('terror-sidebar-layout', TerrorSidebarLayout);
})();
